import React, { useEffect, useState } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { api, formatApiError } from '@/App';
import { Gear, Buildings, Bank, FloppyDisk, Image as ImageIcon } from '@phosphor-icons/react';

const Field = ({ label, value, onChange, testid, type = 'text', placeholder }) => (
  <div>
    <Label className="text-sm font-semibold">{label}</Label>
    <Input
      type={type}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="rounded-none mt-1"
      data-testid={testid}
    />
  </div>
);

const AdminInvoiceSettings = () => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [logoBust, setLogoBust] = useState(Date.now());

  const load = async () => {
    try {
      const { data } = await api.get('/invoicing/settings');
      setSettings({ ...data, vat_rate_pct: Math.round((data.vat_rate ?? 0.15) * 10000) / 100 });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); }, []);

  const set = (key) => (value) => setSettings((s) => ({ ...s, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        company_name: settings.company_name,
        trading_name: settings.trading_name,
        registration_number: settings.registration_number,
        vat_number: settings.vat_number,
        address_lines: settings.address_lines,
        phone: settings.phone,
        email: settings.email,
        website: settings.website,
        bank_name: settings.bank_name,
        account_name: settings.account_name,
        account_number: settings.account_number,
        branch_code: settings.branch_code,
        account_type: settings.account_type,
        footer_note: settings.footer_note,
        footer_terms: settings.footer_terms,
        invoice_prefix: settings.invoice_prefix,
        vat_rate: (Number(settings.vat_rate_pct) || 0) / 100,
        payment_terms_days: Number(settings.payment_terms_days) || 30,
        next_number: Number(settings.next_number) || 1,
      };
      await api.put('/invoicing/settings', payload);
      toast.success('Invoicing settings saved');
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post('/invoicing/settings/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Logo uploaded — it will appear on new invoice PDFs');
      setLogoBust(Date.now());
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!settings) {
    return (
      <AdminLayout title="Invoicing Settings">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-[#4CAF50] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Invoicing Settings">
      <div className="space-y-6 fade-in max-w-4xl">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#0A192F] flex items-center gap-2">
              <Gear className="w-6 h-6 text-[#4CAF50]" /> Invoicing Settings
            </h1>
            <p className="text-sm text-gray-500 mt-1">These details appear on every invoice PDF you generate</p>
          </div>
          <Button onClick={save} disabled={saving} className="bg-[#4CAF50] hover:bg-[#45a049] text-white rounded-none" data-testid="save-invoice-settings-btn">
            <FloppyDisk className="w-4 h-4 mr-2" /> {saving ? 'Saving…' : 'Save Settings'}
          </Button>
        </div>

        <Card>
          <CardHeader className="border-b border-gray-200 pb-4">
            <CardTitle className="text-base font-semibold text-[#0A192F] flex items-center gap-2">
              <Buildings className="w-5 h-5 text-[#0055FF]" /> Company Details
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Company name" value={settings.company_name} onChange={set('company_name')} testid="settings-company-name" />
              <Field label="Registered / trading name" value={settings.trading_name} onChange={set('trading_name')} testid="settings-trading-name" />
              <Field label="Company registration number" value={settings.registration_number} onChange={set('registration_number')} testid="settings-reg-number" placeholder="e.g. 2019/123456/07" />
              <Field label="VAT number" value={settings.vat_number} onChange={set('vat_number')} testid="settings-vat-number" placeholder="e.g. 4123456789" />
              <Field label="Phone" value={settings.phone} onChange={set('phone')} testid="settings-phone" />
              <Field label="Email" value={settings.email} onChange={set('email')} testid="settings-email" />
              <Field label="Website" value={settings.website} onChange={set('website')} testid="settings-website" />
            </div>
            <div>
              <Label className="text-sm font-semibold">Address (one line per row)</Label>
              <Textarea value={settings.address_lines ?? ''} onChange={(e) => set('address_lines')(e.target.value)} rows={3} className="rounded-none mt-1" data-testid="settings-address" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="border-b border-gray-200 pb-4">
            <CardTitle className="text-base font-semibold text-[#0A192F] flex items-center gap-2">
              <Bank className="w-5 h-5 text-[#4CAF50]" /> Banking Details
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6 grid sm:grid-cols-2 gap-4">
            <Field label="Bank" value={settings.bank_name} onChange={set('bank_name')} testid="settings-bank-name" />
            <Field label="Account name" value={settings.account_name} onChange={set('account_name')} testid="settings-account-name" />
            <Field label="Account number" value={settings.account_number} onChange={set('account_number')} testid="settings-account-number" />
            <Field label="Branch code" value={settings.branch_code} onChange={set('branch_code')} testid="settings-branch-code" />
            <Field label="Account type" value={settings.account_type} onChange={set('account_type')} testid="settings-account-type" placeholder="e.g. Business Cheque" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="border-b border-gray-200 pb-4">
            <CardTitle className="text-base font-semibold text-[#0A192F]">Invoice Defaults</CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            <div className="grid sm:grid-cols-4 gap-4">
              <Field label="Number prefix" value={settings.invoice_prefix} onChange={set('invoice_prefix')} testid="settings-prefix" />
              <Field label="Next number" type="number" value={settings.next_number} onChange={set('next_number')} testid="settings-next-number" />
              <Field label="VAT %" type="number" value={settings.vat_rate_pct} onChange={set('vat_rate_pct')} testid="settings-vat-rate" />
              <Field label="Payment terms (days)" type="number" value={settings.payment_terms_days} onChange={set('payment_terms_days')} testid="settings-terms-days" />
            </div>
            <p className="text-xs text-gray-500">
              Next invoice will be numbered <strong>{(settings.invoice_prefix || 'MC')}-{new Date().getFullYear()}-{String(Number(settings.next_number) || 1).padStart(4, '0')}</strong>
            </p>
            <div>
              <Label className="text-sm font-semibold">Footer note on invoices</Label>
              <Textarea value={settings.footer_note ?? ''} onChange={(e) => set('footer_note')(e.target.value)} rows={2} className="rounded-none mt-1" data-testid="settings-footer-note" />
            </div>
            <div>
              <Label className="text-sm font-semibold">Standard terms printed on every invoice</Label>
              <Textarea value={settings.footer_terms ?? ''} onChange={(e) => set('footer_terms')(e.target.value)} rows={4} className="rounded-none mt-1" data-testid="settings-footer-terms" />
              <p className="text-xs text-gray-500 mt-1">One line per term — printed above the footer note on every generated invoice PDF.</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="border-b border-gray-200 pb-4">
            <CardTitle className="text-base font-semibold text-[#0A192F] flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-[#0055FF]" /> Invoice Logo
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            {settings.has_logo && (
              <img
                src={`/api/invoicing/settings/logo?v=${logoBust}`}
                alt="Invoice logo"
                className="h-16 object-contain border border-gray-200 p-2 bg-white"
                data-testid="settings-logo-preview"
              />
            )}
            <Input type="file" accept="image/png,image/jpeg" onChange={(e) => uploadLogo(e.target.files?.[0])} className="rounded-none max-w-sm" data-testid="settings-logo-input" />
            <p className="text-xs text-gray-500">PNG or JPEG, up to 3MB. Appears at the top-left of every generated invoice.</p>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminInvoiceSettings;
